import React, { useState, useEffect, useCallback } from 'react';
import { FaArrowLeft, FaCalculator, FaArrowRight, FaChartLine, FaDollarSign, FaExchangeAlt, FaMoneyBillWave, FaMoneyCheck, FaSyncAlt, FaExpand } from 'react-icons/fa';
import { useFireAuth } from '../FireAuthContext';
import { fetchBitcoinPrice, fetchStockPrices, buyCrypto, sellCrypto, buyStock, sellStock, fetchUserHoldings, fetchUserInvestments } from './CryptoFunctions';
import StockChart from './StockChart';
import ExtendedStockChart from './ExtendedStockChart';
import DelayRender from './DelayRender';
import './Crypto.css';
import { truncateToTwoDecimals } from '../utils/FormatUtils';

const calculatePercentageChange = (purchasePrice, currentPrice) => {
  const change = ((currentPrice - purchasePrice) / purchasePrice) * 100;
  return truncateToTwoDecimals(change);
};

const Crypto = () => {
  const { user, updateUser } = useFireAuth();
  const [bitcoinPrice, setBitcoinPrice] = useState(0);
  const [stocks, setStocks] = useState([]);
  const [selectedStock, setSelectedStock] = useState('HC');
  const [currentPrice, setCurrentPrice] = useState(0);
  const [buyAmount, setBuyAmount] = useState('');
  const [sellAmount, setSellAmount] = useState('');
  const [buyCost, setBuyCost] = useState(0);
  const [sellEarnings, setSellEarnings] = useState(0);
  const [investments, setInvestments] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [ownedStocks, setOwnedStocks] = useState({});
  const [error, setError] = useState('');
  const [lifetimeProfitLoss, setLifetimeProfitLoss] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchPrice = useCallback(async () => {
    const price = await fetchBitcoinPrice();
    setBitcoinPrice(truncateToTwoDecimals(price / 500));
  }, []);

  const fetchStocks = useCallback(async () => {
    const stocksData = await fetchStockPrices();
    setStocks(stocksData);
  }, []);

  const fetchUserData = useCallback(async () => {
    if (user) {
      const userData = await fetchUserHoldings(user.uid);
      const stockTotals = {};

      if (userData.crypto?.hahumCoin) {
        stockTotals['HC'] = userData.crypto.hahumCoin;
      }

      (userData.stocks || []).forEach(stock => {
        stockTotals[stock.symbol] = stock.amount;
      });

      setOwnedStocks(stockTotals);
    }
  }, [user]);

  const fetchUserInvestmentsData = useCallback(async () => {
    if (user) {
      const investmentsData = await fetchUserInvestments(user.uid);
      investmentsData.sort((a, b) => b.timestamp.toMillis() - a.timestamp.toMillis());
      setInvestments(investmentsData);
    }
  }, [user]);

  useEffect(() => {
    fetchPrice();
    fetchStocks();
  }, [fetchPrice, fetchStocks]);

  useEffect(() => {
    fetchUserData();
    fetchUserInvestmentsData();
  }, [user, fetchUserData, fetchUserInvestmentsData]);

  useEffect(() => {
    const getCurrentPrice = () => {
      if (selectedStock === 'HC') {
        return bitcoinPrice;
      } else {
        const stock = stocks.find(s => s.symbol === selectedStock);
        return stock ? truncateToTwoDecimals(stock.prices[stock.prices.length - 1].price) : 0;
      }
    };

    setCurrentPrice(getCurrentPrice());
  }, [selectedStock, bitcoinPrice, stocks]);

  useEffect(() => {
    setBuyCost(truncateToTwoDecimals(parseFloat(buyAmount || 0) * currentPrice));
    setSellEarnings(truncateToTwoDecimals(parseFloat(sellAmount || 0) * currentPrice));
  }, [buyAmount, sellAmount, currentPrice]);

  const handleBuy = async () => {
    if (user) {
      try {
        const livePrice = selectedStock === 'HC' ? truncateToTwoDecimals(await fetchBitcoinPrice() / 500) : truncateToTwoDecimals((await fetchStockPrices()).find(s => s.symbol === selectedStock).prices.slice(-1)[0].price);
        if (livePrice !== currentPrice) {
          setCurrentPrice(livePrice);
          alert(`The price has changed. The current price is ${livePrice} ${selectedStock}/💴.`);
          fetchStocks();
          fetchUserData();
          return;
        }
        const confirmPurchase = window.confirm(`The current price is ${livePrice} ${selectedStock}/💴. You are buying ${truncateToTwoDecimals(parseFloat(buyAmount))} ${selectedStock} for a total price of 💴 ${truncateToTwoDecimals(buyAmount * livePrice)}. Do you want to proceed?`);
        if (confirmPurchase) {
          if (selectedStock === 'HC') {
            await buyCrypto(user.uid, truncateToTwoDecimals(parseFloat(buyAmount)), livePrice);
          } else {
            await buyStock(user.uid, selectedStock, truncateToTwoDecimals(parseFloat(buyAmount)), livePrice);
          }
          updateUser();
          fetchUserInvestmentsData();
          fetchStocks();
          fetchUserData();
        }
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleSell = async () => {
    if (user) {
      try {
        const livePrice = selectedStock === 'HC' ? truncateToTwoDecimals(await fetchBitcoinPrice() / 500) : truncateToTwoDecimals((await fetchStockPrices()).find(s => s.symbol === selectedStock).prices.slice(-1)[0].price);
        if (livePrice !== currentPrice) {
          setCurrentPrice(livePrice);
          alert(`The price has changed. The current price is ${livePrice} ${selectedStock}/💴.`);
          fetchStocks();
          fetchUserData();
          return;
        }
        const confirmSale = window.confirm(`You are selling ${truncateToTwoDecimals(parseFloat(sellAmount))} ${selectedStock} for a total price of 💴 ${truncateToTwoDecimals(sellAmount * livePrice)}. Do you want to proceed?`);
        if (confirmSale) {
          if (selectedStock === 'HC') {
            await sellCrypto(user.uid, truncateToTwoDecimals(parseFloat(sellAmount)), livePrice);
          } else {
            await sellStock(user.uid, selectedStock, truncateToTwoDecimals(parseFloat(sellAmount)), livePrice);
          }
          updateUser();
          fetchUserInvestmentsData();
          fetchStocks();
          fetchUserData();
        }
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const getCurrentInvestmentPrice = (ttype) => {
    if (ttype === 'hahumCoin') {
      return bitcoinPrice;
    } else {
      const stock = stocks.find(s => s.symbol === ttype);
      return stock ? truncateToTwoDecimals(stock.prices[stock.prices.length - 1].price) : 'N/A';
    }
  };

  const calculateLifetimeProfitLoss = () => {
    if (investments.length === 0) return;

    let totalSpent = 0;
    let totalEarned = 0;

    investments.forEach(inv => {
      if (inv.amount > 0) {
        totalSpent += inv.spent;
      } else {
        totalEarned += inv.earned;
      }
    });

    const totalHoldingsValue = Object.keys(ownedStocks).reduce((total, stock) => {
      const amount = ownedStocks[stock];
      const currentPrice = stock === 'HC' ? bitcoinPrice : getCurrentInvestmentPrice(stock);

      if (amount && currentPrice !== 'N/A') {
        const value = amount * parseFloat(currentPrice);
        return total + value;
      }

      return total;
    }, 0);

    const profitLoss = totalEarned - totalSpent;
    const profitLossIncludingHoldings = profitLoss + totalHoldingsValue;

    setLifetimeProfitLoss({
      total: profitLoss,
      includingHoldings: profitLossIncludingHoldings
    });
  };

  const investmentsPerPage = 5;
  const totalPages = Math.ceil(investments.length / investmentsPerPage);
  const paginatedInvestments = investments.slice(currentPage * investmentsPerPage, (currentPage + 1) * investmentsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) setCurrentPage(currentPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
  };

  const handleBuyAmountChange = (e) => {
    const value = e.target.value;
    if (!isNaN(value) && value >= 0) {
      setBuyAmount(value);
    }
  };

  const handleSellAmountChange = (e) => {
    const value = e.target.value;
    if (!isNaN(value) && value >= 0) {
      setSellAmount(value);
    }
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  if (!user) {
    return <div className="crypto-section">Loading...</div>;
  }

  const totalPortfolioValue = Object.keys(ownedStocks).reduce((total, stock) => {
    const amount = ownedStocks[stock];
    const currentPrice = stock === 'HC' ? bitcoinPrice : getCurrentInvestmentPrice(stock);

    if (amount && currentPrice !== 'N/A') {
      const value = amount * parseFloat(currentPrice);
      return total + value;
    }

    return total;
  }, parseFloat(user.money));

  const selectedStockData = selectedStock === 'HC' ? {
    symbol: 'HC',
    prices: Array(25).fill({ price: bitcoinPrice, timestamp: { toMillis: () => Date.now() } }),
  } : stocks.find(stock => stock.symbol === selectedStock);

  const handleStockClick = async (symbol) => {
    setSelectedStock(symbol);
    await fetchPrice();
    await fetchStocks();
  };

  const userStockHolding = ownedStocks[selectedStock] || 0;

  const canBuy = buyAmount > 0 && buyCost <= user.money;

  return (
    <div className="crypto-section">
      <div className="crypto-container">
        <h2 className="crypto-title">Market</h2>

        {error && <div className="error-message">{error}</div>}

        <div className="crypto-grid">
          <div className="crypto-card user-stats">
            <h3><FaMoneyCheck /> Your Stats</h3>
            <table className="user-stats-table">
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Amount</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Cash</td>
                  <td colSpan="2">💴 {user.money !== undefined ? truncateToTwoDecimals(user.money).toFixed(2) : 'Loading...'}</td>
                </tr>
                {ownedStocks['HC'] && ownedStocks['HC'] > 0 && (
                  <tr>
                    <td>HahumCoin (HC)</td>
                    <td>{truncateToTwoDecimals(ownedStocks['HC']).toFixed(2)}</td>
                    <td>💴 {(truncateToTwoDecimals(ownedStocks['HC']) * bitcoinPrice).toFixed(2)}</td>
                  </tr>
                )}
                {Object.keys(ownedStocks).map(stock => (
                  stock !== 'HC' && ownedStocks[stock] && ownedStocks[stock] > 0 && (
                    <tr key={stock}>
                      <td>{stock}</td>
                      <td>{truncateToTwoDecimals(ownedStocks[stock]).toFixed(2)}</td>
                      <td>💴 {(truncateToTwoDecimals(ownedStocks[stock]) * getCurrentInvestmentPrice(stock)).toFixed(2)}</td>
                    </tr>
                  )
                ))}
                <tr>
                  <td colSpan="2">Total Value</td>
                  <td>💴 {truncateToTwoDecimals(totalPortfolioValue).toFixed(2)}</td>
                </tr>

                <tr>
                  <td colSpan="2">Overall Profit/Loss</td>
                  <td>
                    {lifetimeProfitLoss !== null ? (
                      <>
                        💴 {truncateToTwoDecimals(lifetimeProfitLoss.total).toFixed(2)}
                        <span className="holdings-value">
                          ({truncateToTwoDecimals(lifetimeProfitLoss.includingHoldings).toFixed(2)})
                        </span>
                        <FaCalculator onClick={calculateLifetimeProfitLoss} className="calculate-icon" />
                      </>
                    ) : (
                      <FaCalculator onClick={calculateLifetimeProfitLoss} className="calculate-icon" />
                    )}
                  </td>
                </tr>

              </tbody>
            </table>
          </div>

          <div className="crypto-card current-price">
            <h3>
              <FaDollarSign /> Stock Prices
              <FaSyncAlt className="refresh-icon" onClick={() => { fetchPrice(); fetchStocks(); }} />
            </h3>
            <table className="stock-table">
              <thead>
                <tr>
                  <th>Stock Name</th>
                  <th>Symbol</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                <tr className={selectedStock === 'HC' ? 'selected-row' : ''} onClick={() => handleStockClick('HC')}>
                  <td>HahumCoin</td>
                  <td>HC</td>
                  <td>💴 {bitcoinPrice}</td>
                </tr>
                {stocks.map(stock => (
                  <tr key={stock.symbol} className={selectedStock === stock.symbol ? 'selected-row' : ''} onClick={() => handleStockClick(stock.symbol)}>
                    <td>{stock.name}</td>
                    <td>{stock.symbol}</td>
                    <td>💴 {stock.prices && stock.prices.length ? stock.prices[stock.prices.length - 1].price.toFixed(2) : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="crypto-card stock-chart">
              <h3>
                <FaChartLine /> Price Chart
                <FaExpand className="expand-icon" onClick={handleModalOpen} />
              </h3>
              {selectedStockData && <StockChart stock={selectedStockData} />}
            </div>
          </div>

          <div className="crypto-card-row">
            <div className="crypto-card buy-sell">
              <h3><FaMoneyBillWave /> Buy</h3>
              <select value={selectedStock} onChange={(e) => setSelectedStock(e.target.value)} className="crypto-select">
                <option value="HC">HC</option>
                {stocks.map(stock => (
                  <option key={stock.symbol} value={stock.symbol}>{stock.symbol}</option>
                ))}
              </select>
              <input
                type="text"
                value={buyAmount}
                onChange={handleBuyAmountChange}
                placeholder={`Amount of ${selectedStock}`}
                className="crypto-input"
              />
              <p className="crypto-cost">You own {truncateToTwoDecimals(userStockHolding).toFixed(2)}</p>
              <button onClick={handleBuy} disabled={!canBuy} className="crypto-button"><FaArrowRight /></button>
              <p className="crypto-cost">Cost: 💴 {buyCost.toFixed(2)}</p>
            </div>

            <div className="crypto-card buy-sell">
              <h3><FaMoneyBillWave /> Sell</h3>
              <select value={selectedStock} onChange={(e) => setSelectedStock(e.target.value)} className="crypto-select">
                <option value="HC">HC</option>
                {stocks.map(stock => (
                  <option key={stock.symbol} value={stock.symbol}>{stock.symbol}</option>
                ))}
              </select>
              <input
                type="text"
                value={sellAmount}
                onChange={handleSellAmountChange}
                placeholder={`Amount of ${selectedStock}`}
                className={`crypto-input ${parseFloat(sellAmount) > userStockHolding ? 'input-error' : ''}`}
              />
              <p className="crypto-cost">You own {truncateToTwoDecimals(userStockHolding).toFixed(2)}</p>
              <button onClick={handleSell} disabled={sellAmount <= 0 || parseFloat(sellAmount) > userStockHolding} className="crypto-button"><FaArrowLeft /></button>
              <p className="crypto-cost">Earn: 💴 {sellEarnings.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="crypto-container">
        <div className="crypto-card investments">
          <h3><FaExchangeAlt /> Order History</h3>
          <table className="investment-table">
            <thead>
              <tr>
                <th>Stock</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Price</th>
                <th>Current Price</th>
                <th>Total</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {paginatedInvestments.map((inv, index) => (
                <tr key={index}>
                  <td>{inv.ttype === 'hahumCoin' ? 'HC' : inv.ttype}</td>
                  <td>{inv.amount > 0 ? 'BUY' : 'SELL'}</td>
                  <td>{truncateToTwoDecimals(Math.abs(inv.amount)).toFixed(2)}</td>
                  <td>{truncateToTwoDecimals(inv.price).toFixed(2)}</td>
                  <td>
                    {getCurrentInvestmentPrice(inv.ttype)} 
                    <span className={`percentage-change ${(inv.amount > 0 && calculatePercentageChange(inv.price, getCurrentInvestmentPrice(inv.ttype)) >= 0) || (inv.amount < 0 && calculatePercentageChange(inv.price, getCurrentInvestmentPrice(inv.ttype)) < 0) ? 'positive' : 'negative'}`}>
                      {calculatePercentageChange(inv.price, getCurrentInvestmentPrice(inv.ttype))}%
                    </span>
                  </td>
                  <td>{inv.amount > 0 ? `-💴 ${truncateToTwoDecimals(inv.spent).toFixed(2)}` : `+💴 ${truncateToTwoDecimals(inv.earned).toFixed(2)}`}</td>
                  <td>{inv.timestamp && inv.timestamp.toDate ? inv.timestamp.toDate().toLocaleString() : 'Invalid date'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            <button onClick={handlePreviousPage} disabled={currentPage === 0} className="pagination-button"><FaArrowLeft /></button>
            <button onClick={handleNextPage} disabled={currentPage === totalPages - 1} className="pagination-button"><FaArrowRight /></button>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <ExtendedStockChart stock={selectedStockData} onClose={handleModalClose} />
      )}
    </div>
  );
};

export default DelayRender(Crypto, 300);
